import * as React from 'react';

import { Input } from '@/components/ui/input';

/**
 * A number input that does NOT change its value on mouse wheel.
 *
 * THIS IS A SILENT-DATA-CHANGE DEFECT, NOT TIDINESS (Ashok ruling,
 * 2026-08-19; UX backlog item 3, which recorded it as the latter). A
 * focused `type="number"` input increments on wheel in every major browser.
 * A buyer who has just typed an estimated budget and then scrolls the page
 * with the cursor still over the field silently changes the amount - no
 * keystroke, no cue, no undo affordance. The budget drives
 * estimated_spend_usd, which drives the spend-threshold band, which drives
 * risk banding and financial-review applicability. The same wheel over a
 * line quantity changes what a vendor is asked to price.
 *
 * THE SUPPRESSION IS preventDefault WHILE FOCUSED, and the alternative was
 * considered and rejected. The widely-copied fix is `onWheel={e =>
 * e.target.blur()}`: it works, and it lets the page keep scrolling, but it
 * rips focus out of the field on a scroll. That breaks keyboard flow for
 * exactly the users who depend on it most, and the ruling was explicit that
 * accessibility must not pay for this. preventDefault keeps focus, keeps
 * the arrow keys, keeps step/min/max, and is invisible to a screen reader.
 *
 * THE COST, STATED: while the field HAS focus AND the pointer is over it,
 * the wheel no longer scrolls the page either. That is a narrow window -
 * move the pointer off the field, or click elsewhere, and scrolling is
 * normal - and it is the deliberate price of never changing a number the
 * user did not type.
 *
 * A NATIVE, NON-PASSIVE LISTENER, NOT React's onWheel. React attaches its
 * wheel handling at the root as a PASSIVE listener, and preventDefault() in
 * a passive listener does nothing (the browser logs an "Unable to preventDefault
 * inside passive event listener" warning and increments anyway). The
 * listener therefore has to be attached to the element itself with
 * `{ passive: false }`. A test that mounted this and fired a synthetic
 * React wheel event would pass against a version that does not work in a
 * browser, so the test dispatches a real DOM WheelEvent.
 *
 * ONLY WHILE FOCUSED, deliberately: an unfocused number input does not
 * increment on wheel to begin with, so blocking the page scroll over one
 * would be a cost with no benefit.
 *
 * Everything else is the shared `Input` - className, disabled, min, max,
 * step, data-testid and the rest pass straight through, so converting a
 * site is a one-word change and nothing about its appearance moves.
 */
const NumberInput = React.forwardRef(({ onWheel, ...props }, ref) => {
  const innerRef = React.useRef(null);

  // Callers keep whatever ref they had; this component needs its own handle
  // on the DOM node to attach a non-passive listener.
  React.useImperativeHandle(ref, () => innerRef.current, []);

  React.useEffect(() => {
    const element = innerRef.current;
    if (!element) return undefined;

    const suppressWheelIncrement = (event) => {
      if (document.activeElement !== element) return;
      event.preventDefault();
    };

    element.addEventListener('wheel', suppressWheelIncrement, { passive: false });
    return () => element.removeEventListener('wheel', suppressWheelIncrement);
  }, []);

  return <Input {...props} type="number" ref={innerRef} onWheel={onWheel} />;
});
NumberInput.displayName = 'NumberInput';

export { NumberInput };
